import React, { useMemo, useState } from "react";
import { api, roleLabel } from "@/lib/api";
import { X, Search, Check } from "lucide-react";
import { toast } from "sonner";

// Compose an ISO string from local date+time inputs. Falls back to date only for all-day.
function combineLocal(dateStr, timeStr, allDay) {
  if (!dateStr) return "";
  if (allDay) {
    return new Date(`${dateStr}T00:00`).toISOString();
  }
  return new Date(`${dateStr}T${timeStr || "09:00"}`).toISOString();
}

function toLocalDate(iso) {
  if (!iso) return "";
  try { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; } catch { return ""; }
}
function toLocalTime(iso) {
  if (!iso) return "";
  try { const d = new Date(iso); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`; } catch { return ""; }
}

export default function EventDialog({ mode = "create", event = null, directory, defaultDate, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const initialDate = event
    ? toLocalDate(event.start_at)
    : (defaultDate ? `${defaultDate.getFullYear()}-${String(defaultDate.getMonth()+1).padStart(2,"0")}-${String(defaultDate.getDate()).padStart(2,"0")}` : "");

  const [title, setTitle] = useState(event?.title || "");
  const [description, setDescription] = useState(event?.description || "");
  const [dateStr, setDateStr] = useState(initialDate);
  const [startTime, setStartTime] = useState(event ? toLocalTime(event.start_at) : "09:00");
  const [endTime, setEndTime] = useState(event ? toLocalTime(event.end_at) : "10:00");
  const [allDay, setAllDay] = useState(event?.all_day || false);
  const [location, setLocation] = useState(event?.location || "");
  const [boutiqueCode, setBoutiqueCode] = useState(event?.boutique_code || "");

  // Visibility scope
  const initialScope = (() => {
    if (!event) return "boutique_role"; // sensible default
    const v = event.visibility || {};
    if (v.everyone) return "org";
    if ((v.user_ids || []).length && !v.roles?.length && !v.boutiques?.length) return "individuals";
    return "boutique_role";
  })();
  const [scope, setScope] = useState(initialScope);
  const [visibleRoles, setVisibleRoles] = useState(new Set(event?.visibility?.roles || []));
  const [visibleBoutiques, setVisibleBoutiques] = useState(new Set(event?.visibility?.boutiques || []));
  const [visibleUserIds, setVisibleUserIds] = useState(new Set(event?.visibility?.user_ids || []));
  const [requiredIds, setRequiredIds] = useState(new Set(event?.required_attendees || []));
  const [saving, setSaving] = useState(false);
  const [attendeeQuery, setAttendeeQuery] = useState("");

  const toggleSet = (setter, val) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  };

  const filteredUsers = useMemo(() => {
    const q = attendeeQuery.trim().toLowerCase();
    const all = directory.users || [];
    if (!q) return all;
    return all.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q) ||
      (u.boutique_code || "").toLowerCase().includes(q)
    );
  }, [attendeeQuery, directory.users]);

  const save = async () => {
    if (!title.trim()) { toast.error("Give the event a title."); return; }
    if (!dateStr) { toast.error("Pick a date."); return; }
    const start_at = combineLocal(dateStr, startTime, allDay);
    const end_at = combineLocal(dateStr, allDay ? "23:59" : endTime, false);
    if (new Date(end_at) < new Date(start_at)) { toast.error("End must be after start."); return; }

    const visibility = {
      everyone: scope === "org",
      roles: scope === "boutique_role" ? Array.from(visibleRoles) : [],
      boutiques: scope === "boutique_role" ? Array.from(visibleBoutiques) : [],
      user_ids: scope === "individuals" ? Array.from(visibleUserIds) : [],
    };

    const payload = {
      title: title.trim(),
      description: description.trim(),
      start_at, end_at,
      all_day: allDay,
      location: location.trim(),
      boutique_code: boutiqueCode || null,
      visibility,
      required_attendees: Array.from(requiredIds),
    };
    setSaving(true);
    try {
      const res = isEdit
        ? await api.patch(`/calendar/events/${event.id}`, payload)
        : await api.post("/calendar/events", payload);
      toast.success(isEdit ? "Event updated." : "Event created.");
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap"
      onClick={onClose}
      data-testid="event-dialog"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto card-shadow mobile-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Calendar</div>
            <h2 className="font-serif-display text-xl text-stone-900">{isEdit ? "Edit event" : "New event"}</h2>
          </div>
          <button onClick={onClose} data-testid="event-dialog-close" className="p-2 rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-900">
            <X size={18} strokeWidth={1.5} />
          </button>
        </header>

        <div className="px-6 py-6 space-y-6">
          {/* Title / description */}
          <div className="space-y-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="event-title"
                placeholder="e.g. Q2 GM sync"
                className="w-full px-4 py-2.5 rounded-full subtle-border bg-white text-sm text-stone-900 outline-none focus:border-stone-400"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="event-description"
                rows={3}
                placeholder="What is this event about?"
                className="w-full px-4 py-2.5 rounded-2xl subtle-border bg-white text-sm text-stone-900 outline-none focus:border-stone-400 resize-none"
              />
            </div>
          </div>

          {/* When / where */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Date</label>
              <input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="event-date" className="w-full px-3 py-2 rounded-xl subtle-border text-sm" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Start</label>
              <input type="time" value={startTime} disabled={allDay} onChange={(e) => setStartTime(e.target.value)} data-testid="event-start-time" className="w-full px-3 py-2 rounded-xl subtle-border text-sm disabled:bg-stone-50 disabled:text-stone-400" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">End</label>
              <input type="time" value={endTime} disabled={allDay} onChange={(e) => setEndTime(e.target.value)} data-testid="event-end-time" className="w-full px-3 py-2 rounded-xl subtle-border text-sm disabled:bg-stone-50 disabled:text-stone-400" />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-stone-700">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} data-testid="event-all-day" />
            All day
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Location</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="event-location" placeholder="e.g. PLT staff lounge" className="w-full px-4 py-2 rounded-full subtle-border text-sm" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Host boutique (optional)</label>
              <select value={boutiqueCode || ""} onChange={(e) => setBoutiqueCode(e.target.value)} data-testid="event-boutique" className="w-full px-4 py-2 rounded-full subtle-border text-sm">
                <option value="">— none —</option>
                {(directory.boutiques || []).map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Visibility */}
          <section className="rounded-2xl border border-stone-100 p-4" data-testid="event-visibility">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-eyebrow">Who needs to know</div>
                <div className="text-[11px] text-stone-500 mt-0.5">People below will see this event on their calendar.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: "org", label: "Everyone (org-wide)" },
                { key: "boutique_role", label: "By boutique / role" },
                { key: "individuals", label: "Specific people" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setScope(opt.key)}
                  data-testid={`event-scope-${opt.key}`}
                  className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${
                    scope === opt.key
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {scope === "boutique_role" && (
              <div className="space-y-3">
                <div>
                  <div className="label-eyebrow mb-1.5">Boutiques</div>
                  <div className="flex flex-wrap gap-2">
                    {(directory.boutiques || []).map((b) => {
                      const on = visibleBoutiques.has(b);
                      return (
                        <button
                          key={b}
                          onClick={() => toggleSet(setVisibleBoutiques, b)}
                          data-testid={`event-vis-boutique-${b}`}
                          className={`px-3 py-1 rounded-full text-xs border ${on ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
                        >
                          {b}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow mb-1.5">Roles</div>
                  <div className="flex flex-wrap gap-2">
                    {(directory.roles || []).map((r) => {
                      const on = visibleRoles.has(r);
                      return (
                        <button
                          key={r}
                          onClick={() => toggleSet(setVisibleRoles, r)}
                          data-testid={`event-vis-role-${r}`}
                          className={`px-3 py-1 rounded-full text-[11px] border ${on ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
                        >
                          {roleLabel[r] || r}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {scope === "individuals" && (
              <PeoplePicker
                label="Select specific people who can see this"
                users={directory.users}
                selected={visibleUserIds}
                onToggle={(uid) => toggleSet(setVisibleUserIds, uid)}
                testid="event-vis-users"
              />
            )}
          </section>

          {/* Required attendees */}
          <section className="rounded-2xl border border-amber-100 bg-amber-50/40 p-4" data-testid="event-required">
            <div className="mb-3">
              <div className="label-eyebrow text-amber-900">Who must attend</div>
              <div className="text-[11px] text-stone-500 mt-0.5">Required attendees will be asked to RSVP. They always see this event, even if not in the audience above.</div>
            </div>
            <div className="mb-2">
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  value={attendeeQuery}
                  onChange={(e) => setAttendeeQuery(e.target.value)}
                  data-testid="event-required-search"
                  placeholder="Search people…"
                  className="w-full pl-8 pr-3 py-1.5 rounded-full subtle-border text-xs bg-white"
                />
              </div>
            </div>
            <PeoplePicker
              users={filteredUsers}
              selected={requiredIds}
              onToggle={(uid) => toggleSet(setRequiredIds, uid)}
              testid="event-required-users"
              compact
            />
          </section>
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-stone-100 px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="event-cancel" className="px-5 py-2 rounded-full text-sm text-stone-600 hover:bg-stone-100">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="event-save"
            className="px-5 py-2 rounded-full text-sm bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create event")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function PeoplePicker({ label, users, selected, onToggle, testid, compact = false }) {
  return (
    <div>
      {label && <div className="label-eyebrow mb-2">{label}</div>}
      <div className={`grid ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"} gap-1.5 max-h-64 overflow-y-auto pr-1`} data-testid={testid}>
        {(users || []).map((u) => {
          const on = selected.has(u.user_id);
          return (
            <button
              key={u.user_id}
              onClick={() => onToggle(u.user_id)}
              data-testid={`${testid}-${u.user_id}`}
              className={`flex items-center gap-2 text-left px-2.5 py-1.5 rounded-xl border text-xs transition-colors ${
                on ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-white border-stone-100 hover:border-stone-300 text-stone-700"
              }`}
            >
              <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${on ? "bg-emerald-500 border-emerald-500 text-white" : "border-stone-300"}`}>
                {on && <Check size={10} strokeWidth={3} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">{u.name}</span>
                <span className="block text-[10px] text-stone-500 truncate">
                  {(u.boutique_code || "RNB")} · {roleLabel[u.role] || u.role}
                </span>
              </span>
            </button>
          );
        })}
        {(users || []).length === 0 && <div className="text-xs text-stone-400 italic px-2">No matches</div>}
      </div>
    </div>
  );
}
